import { useRef } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { hideProjectApi, unhideProjectApi } from "../../../services/api";
import { setSnackbar } from "../../../redux/messageSlice";
import { updateState } from "../../../redux/commonSlice";
import { showSnackBar } from "../../../utils/commonServices";

// AP-02: the API speaks hide/unhide; the user must only ever read "archived".
// The backend messages now say "archived"/"unarchived" too, but these stay
// frontend-owned: the wording is then the same on every environment, whether or
// not the fix is deployed there.
export const ARCHIVE_SUCCESS_MESSAGE = "Project archived successfully";
export const UNARCHIVE_SUCCESS_MESSAGE = "Project unarchived successfully";

// AP-06 / AP-07 / AP-09: event_id is now validated server-side - an array, a
// float, a padded string or a missing value all come back as a 422 instead of
// quietly archiving something. Send a plain positive integer or do not call at
// all, so a bad id surfaces as nothing happening rather than as a validation
// error the user cannot act on.
export const normalizeEventId = (eventId) => {
  if (typeof eventId === "number") {
    return Number.isInteger(eventId) && eventId > 0 ? eventId : null;
  }
  if (typeof eventId === "string" && /^\d+$/.test(eventId.trim())) {
    const parsed = parseInt(eventId.trim(), 10);
    return parsed > 0 ? parsed : null;
  }
  return null;
};

/**
 * Per-user archive / unarchive of a project.
 *
 * The API calls these hide/unhide; "archive" is the name the UI uses.
 *
 * On success we refetch rather than splice the array: the stage counts, the
 * filter results and archived_count all have to recompute from what the server
 * now returns.
 *
 * @param refetchList called after a successful archive/unarchive
 */
export default (refetchList) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // AP-01 is fixed server-side - overlapping `hide` requests for the same project
  // now all return 200 instead of the loser racing the unique index into an HTML
  // 500. The guard stays: a double-click still has no reason to make two calls,
  // and it keeps the UI correct on environments that do not have the fix yet.
  // Disabling the button does not close the gap - the second click lands before
  // React re-renders it, so the guard has to be synchronous. Keyed by operation +
  // id so archiving one project never blocks acting on a different one.
  const inFlight = useRef(new Set());

  // useEventList caches the same endpoint under `eventsList<consent_id>`; drop it
  // or an archived project stays on screen until a hard refresh.
  const invalidateEventsList = () => {
    queryClient.invalidateQueries({
      predicate: (query) => `${query.queryKey?.[0]}`.startsWith("eventsList"),
    });
  };

  const closePopup = () => {
    dispatch(updateState({ archive_project_popup: false, archive_project_name: "", archive_project_id: null }));
  };

  const settle = (key) => {
    inFlight.current.delete(key);
  };

  const handleSuccess = (response, key, message) => {
    settle(key);
    closePopup();
    if (response?.data?.http_code === 200) {
      dispatch(setSnackbar({ flag: true, type: "success", message }));
      invalidateEventsList();
      refetchList && refetchList();
    } else {
      dispatch(setSnackbar({ flag: true, type: "error", message: "some error occured" }));
    }
  };

  const handleError = (error, key) => {
    settle(key);
    closePopup();
    showSnackBar(dispatch, error?.response?.data);
  };

  const { mutate: archiveMutate, status: archiveStatus } = useMutation(hideProjectApi, {
    onSuccess: (response, variables) =>
      handleSuccess(response, `hide:${variables?.event_id}`, ARCHIVE_SUCCESS_MESSAGE),
    onError: (error, variables) => handleError(error, `hide:${variables?.event_id}`),
  });

  const { mutate: unarchiveMutate, status: unarchiveStatus } = useMutation(unhideProjectApi, {
    onSuccess: (response, variables) =>
      handleSuccess(response, `unhide:${variables?.event_id}`, UNARCHIVE_SUCCESS_MESSAGE),
    onError: (error, variables) => handleError(error, `unhide:${variables?.event_id}`),
  });

  const archiveProject = (eventId) => {
    const id = normalizeEventId(eventId);
    if (id === null) return closePopup();
    const key = `hide:${id}`;
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    archiveMutate({ event_id: id });
  };

  const unarchiveProject = (eventId) => {
    const id = normalizeEventId(eventId);
    if (id === null) return closePopup();
    const key = `unhide:${id}`;
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    unarchiveMutate({ event_id: id });
  };

  return [archiveProject, unarchiveProject, archiveStatus, unarchiveStatus];
};
